import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Image, FlatList, ActivityIndicator, ScrollView } from 'react-native';
import type { AthleteProps, BEEventProps, BestAvailableOrderProps, CompanyProps, CompetitionProps, EventOrderStatProps, EventProps, GuideResponseProps, LeagueProps, MarketProps, MarketSideOptionProps, OrderProps, ParlayProps, PodcastProps, PollProps, SquaresCompetitionProps, TradeProps } from '../../../types';
import { view_styles } from '../../../constants/styles';
import { Icons, LinearDiagnal } from '../../../Components';
import {TeamEventMarketHelpers} from './api';
import MarketButton from '../MarketButton';
import Colors from '../../../constants/colors';
import AthleteMarket from '../AthleteMarket';
import EventMarket from '../EventMarket';

import { MarketComponentApi, MarketComponentHelpers } from '../../api';
import { MarketButtonHelpers } from '../MarketButton/api';
import { LinearGradient } from 'expo-linear-gradient';
import { useIsInViewport } from '../../../Components/ViewportObserver';
import ContestStatsCard from '../ContestStats';
import PodcastCard from '../PodcastCard';
import { Button, Text, View } from '../../../Components/Themed';
import { useColors } from '../../../constants/useColors';
import OrderGradeBar from '../OrderGradeBar';
import moment from 'moment-mini';
import { GuideView } from 'be-components';
import GuideButton from '../../../Guide/GuideButton';

type TeamEventMarketProps = {
    event:EventProps,
    markets:MarketProps[],
    parlays?:ParlayProps[],
    init_expanded?:boolean,
    show_help?:boolean,
    onHelpToggle?:(visible:boolean) => void,
    help_location?:'bottom'|'top',
    onViewMarketStats?:() => void,
    onSetMarket?:(market_id:string, side_type?:string, side_id?:string) => void,
    league?:LeagueProps,
    competitions?:CompetitionProps[],
    show_id?:string,
    disabled_markets?:string[],
    hide_liquidity?:boolean,
    float?:boolean,
    hot_markets_direction?:'vertical'|'horizontal'
    show_podcasts?:boolean,
    squares_competitions?:SquaresCompetitionProps[],
    latest_trades:TradeProps[],
    style?:any,
    onParlaySelect?:(parlay:ParlayProps) => void,
    flash_markets?:PollProps[],
    best_available_orders:BestAvailableOrderProps[],
    event_order_stats:EventOrderStatProps[],
    default_price_view: 'last_trade'|'best_available',
    onView:(data:{ event_id:string, event_type:string, market_id:string, side_type?:string, side_id?:string }) => void,
    onOrder:(order:OrderProps) => void,
    onTVSelect?:(url:string) => void,
    onEvent?:(be_event:BEEventProps) => void,
    onExpand?:(expanded:boolean) => void,
    onTeamSelect?:(team_id:string) => void,
    onFlashSelect?:(poll_id:string) => void,
    onShare?:(event:EventProps) => void,
    onPodcastSelect?:(podcast:PodcastProps) => void,
    show_grades?:boolean,
    highlight_router_orders?:boolean,
    hide_non_primary?:boolean,
    onCompetitionSelect:(competition_id:string) => void,
    onSquaresSelect: (sq_comp_id:string) => void,
    onViewAdditionalMarkets: (event_id:string, event_type:string) => void,
    onTradeLongPress:(trade:TradeProps) => void,
    onActivate?:(event_id:string, event_type:string) => void
    activate_loading?:boolean
}
const TeamEventMarket = ({ event, show_help, highlight_router_orders, help_location, parlays, hide_non_primary, disabled_markets, flash_markets, league, float, style, init_expanded, hot_markets_direction, show_id, hide_liquidity, show_podcasts, activate_loading, competitions, squares_competitions, show_grades, markets, latest_trades, best_available_orders, event_order_stats, default_price_view, onHelpToggle, onSetMarket, onTeamSelect, onFlashSelect, onParlaySelect, onSquaresSelect, onCompetitionSelect, onExpand, onOrder, onViewMarketStats, onView, onViewAdditionalMarkets, onTradeLongPress, onTVSelect, onActivate, onEvent, onShare, onPodcastSelect }:TeamEventMarketProps) => {
    const C = useColors();
    const [ market_width, setMarketWidth ] = useState(180);
    const [ guide_response, setGuide ] = useState<{guide_active:boolean, guide?:GuideResponseProps}>({ guide_active:false });
    const { guide_active, guide } = guide_response;
    const [ expanded_data, setExpandedData ] = useState<{
        expanded:boolean,
        loaded:boolean,
        loading:boolean,
        selected_flash?:string,
        podcasts:PodcastProps[],
        athletes:AthleteProps[]
    }>({
        loaded:false,
        loading:false,
        expanded:false,
        athletes:[],
        podcasts: []
    })
    const [ show_stats, setShowStats ] = useState(false);
    const [ sponsor, setSponsor ] = useState<CompanyProps|undefined>(undefined);
    const [ competitions_expanded, setCompetitionsExpanded ] = useState(false);
    const [ parlays_expanded, setParlaysExpanded ] = useState(false);
    const [ squares_expanded, setSquaresExpanded ] = useState(false);
    const { expanded, selected_flash, athletes, loaded, loading, podcasts } = expanded_data;
    const event_status = TeamEventMarketHelpers.getEventStatus(event);
    const upcoming_flash_market = TeamEventMarketHelpers.getUpcomingFlash(flash_markets, selected_flash);

    const ref = useRef(null)
    const isInViewport = useIsInViewport(ref)
    useEffect(() => {
        MarketComponentApi.setEnvironment();
        if(init_expanded){ 
            expandedEvent(); 
        }
        if(event.sponsor_id){ getCompanyFromServer(event.sponsor_id) }
    },[init_expanded])

    useEffect(() => {
        if(isInViewport && onEvent && event.sponsor_id){
            let be_event:BEEventProps = {
                event_name:'company_view',
                event_data: {
                    view_type: 'contest',
                    view_location: 'list',
                    contest_type:'team',
                    contest_id:event.event_id,
                    company_id: event.sponsor_id
                },
                level:2
            }
            onEvent(be_event)
        }
    },[isInViewport])


    const getCompanyFromServer = async(company_id:string) => {
        const resp = await MarketComponentApi.getCompanyById(company_id)
        if(!resp?.company){ return }
        setSponsor(resp.company)
    }

    const expandedEvent = async() => {
        if(!event){ return }
        if(onExpand){ onExpand(expanded ? false : true) }
        if(expanded){ return setExpandedData({ ...expanded_data, expanded: false }) }
        //Check if we have any non-primary markets sorted
        let pods:PodcastProps[] = []
        if(show_podcasts){
            let key_words = `${event.away?.market_name} ${event.away?.name},${event.home?.market_name} ${event.home?.name}`
            pods = await MarketComponentApi.getPodcastsByKeywords(key_words)
        }

        if(loaded){ return setExpandedData({ ...expanded_data, expanded: true }) }
        setExpandedData({ ...expanded_data, expanded:true, loading:true });
        //Need to load our athletes in here!
        const away_athletes = await MarketComponentApi.getAthletesByTeamId(event.away_team_id);
        const home_athletes = await MarketComponentApi.getAthletesByTeamId(event.home_team_id);
        setExpandedData({
            expanded:true,
            loaded:true,
            loading:false,
            podcasts: pods,
            athletes: away_athletes.concat(home_athletes)
         })
    }
    
    const primary_markets = useMemo(() => {
        return TeamEventMarketHelpers.getPrimaryMarkets(event, markets)
    }, [JSON.stringify(markets)]);

    let primary_width = 180
    if(primary_markets.length > 0){
        primary_width = (market_width - (primary_markets.length * 8)) / (primary_markets.length)
    }
    
    const handleSelectCompetition = () => {
        if(!competitions || competitions.length == 0){ return }
        if(competitions.length == 1 && competitions[0]){ return onCompetitionSelect(competitions[0].competition_id) }
        return setCompetitionsExpanded(true);
    }

    const handleSelectParlay = () => {
        if(!parlays || parlays.length == 0){ return }
        if(!onParlaySelect){ return }
        if(parlays.length == 1 && parlays[0]){ return onParlaySelect(parlays[0]) }
        return setParlaysExpanded(true);
    }

    const handleSelectSquares = () => {
        if(!squares_competitions || squares_competitions.length == 0){ return }
        if(squares_competitions.length == 1 && squares_competitions[0]){ return onSquaresSelect(squares_competitions[0].sq_comp_id) }
        return setSquaresExpanded(true);
    }

    const renderCompetitions = (data:{ item:CompetitionProps, index: number }) => {
        return (
            <Button 
                type='text'
                style={{ flexDirection:'row', alignItems:'center', borderBottomWidth:1, borderColor:C.borders.light }} 
                onPress={() => {
                    setCompetitionsExpanded(false);
                    onCompetitionSelect(data.item.competition_id)
                }}>
                <Image
                    source={{ uri: data.item.image?.url }}
                    style={{ height:25, width:25 }}
                    resizeMode='cover'
                />
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='description'>{data.item.competition_name}</Text>
                </View>
                <Icons.ChevronIcon direction='right' size={10} color={C.text.h1} />
            </Button>
        )
    }

        const renderParlays = (data:{ item:ParlayProps, index: number }) => {
        return (
            <Button 
                type='text'
                transparent
                style={{ padding:10, flexDirection:'row', alignItems:'center', borderBottomWidth:1, borderColor:C.borders.light, borderRadius:0 }} 
                onPress={() => {
                    if(!onParlaySelect){ return }
                    setParlaysExpanded(false);
                    onParlaySelect(data.item)
                }}>
                <Icons.ParlayIcon size={14} color={C.text.h1} />
                <OrderGradeBar style={{ marginLeft:5 }} grade={data.item.grade} view_type='book'/>
                <View transparent style={{ flex:1, marginLeft:10, marginRight:10 }}>
                    <Text theme='h2'>{data.item.name}</Text>
                </View>
                <Icons.ChevronIcon direction='right' size={10} color={C.text.h1} />
            </Button>
        )
    }

    const renderSquaresCompetitions = (data:{ item:SquaresCompetitionProps, index: number }) => {
        return (
            <Button 
                type='text'
                style={{ flexDirection:'row', alignItems:'center', borderBottomWidth:1, borderColor:C.borders.light }} 
                onPress={() => {
                    setCompetitionsExpanded(false);
                    onSquaresSelect(data.item.sq_comp_id)
                }}>
                <Image
                    source={{ uri: data.item.image?.url }}
                    style={{ height:25, width:25 }}
                    resizeMode='cover'
                />
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='description'>{data.item.sq_comp_name}</Text>
                </View>
                <Icons.ChevronIcon direction='right' size={10} color={C.text.h1} />
            </Button>
        )
    }

    const renderSides = useCallback((data:{ item:MarketSideOptionProps, index:number, market:MarketProps, is_locked:boolean }) => {
        const ba_order = best_available_orders.find(o => o.market_id == data.market.market_id && o.side == data.item.side && o.show)
        const side_details = MarketComponentHelpers.getTeamEventSideIds(event, data.market, data.item);
        if(!side_details){ return <></> } //Bomb out if we cant get the side details
        let trade = MarketButtonHelpers.getVisibleTrade(default_price_view, data.market, data.item, latest_trades, best_available_orders, side_details.side_id, side_details.reversed_side_id);
        if(!trade){
            trade = MarketButtonHelpers.getTradeFromMarket(data.market, event.event_id, 'team', data.item.side, data.item.id_source, side_details.side_id, side_details.reversed_side_id)
        }
        if(!trade){ return <></> }
        let disabled = false
        if(disabled_markets && disabled_markets.includes(data.market.market_id.toString())){ disabled = true }
        return (
            <View transparent key={`${event.event_id}_${data.item.side}`} style={{ minWidth:primary_width }}>
                <View transparent style={{ height:50, margin:2 }}>
                    <MarketButton
                        side_option={data.item}
                        market={data.market}
                        trade={trade}
                        order={ba_order}
                        highlight_router_orders={highlight_router_orders}
                        locked={disabled || data.is_locked}
                        show_grades={show_grades}
                        onLongPress={onTradeLongPress}
                        onPress={(order) => {
                            const order_title = MarketComponentHelpers.getOrderTitleForTeamEvent(order, data.market, event);
                            if(!order_title){ return alert('Unable to process order') }
                            onOrder({ ...order, title: order_title, league_id: event.league_id })
                        }}
                    />
                </View>
                {onSetMarket && data.item.side != data.market.trade_side ?
                <Button
                    title='SET'
                    type='success'
                    style={{ margin:2, padding:8 }}
                    onPress={() => onSetMarket(data.market.market_id)}
                />
                :<></>}
            </View>
        )
    },[JSON.stringify(best_available_orders), JSON.stringify(latest_trades), primary_width, JSON.stringify(disabled_markets), default_price_view])

    const renderMarketHeaders = useCallback((data:{ item:MarketProps, index:number }) => {
        return (
            <View transparent style={{ width:primary_width, marginLeft:2, marginRight:2 }}>
                <Text size={12} color={C.text.h1} textAlign='center'>{data.item.type.toUpperCase()}</Text>
            </View>
        )
    },[primary_markets.length, primary_width])

    const renderPodcasts = useCallback((data:{ item:PodcastProps, index:number }) => {
        return (
            <View style={{ margin:3 }}>
                <PodcastCard
                    podcast={data.item}
                    size={60}
                    onPodcastSelect={(pod) => {
                        if(onPodcastSelect){ onPodcastSelect(pod) }
                    }}
                />
            </View>
        )
    },[podcasts.length])


    const renderNonPrimaryMarkets = useCallback((data: { item:EventOrderStatProps, index:number }) => {
        const market = markets.find(m => m.market_id == data.item.market_id);
        if(!market){ return <></> }
        switch(market.level){
            case 'athlete':
                const athlete = athletes.find(a => a.athlete_id == data.item.side_id)
                if(!athlete){ return <></> }
                const athlete_trades = latest_trades.filter(t => t.market_type == 'FOR_MONEY' && t.market_id == data.item.market_id && t.side_type == 'athlete' && t.side_id == data.item.side_id);
                const athlete_orders = best_available_orders.filter(t => t.market_id == data.item.market_id && t.side_type == 'athlete' && t.side_id == data.item.side_id);
                return (
                        <AthleteMarket
                            event={event}
                            event_type='team'
                            athlete={athlete}
                            float
                            highlight_router_orders={highlight_router_orders}
                            disabled_markets={disabled_markets}
                            style={{ flexGrow:1, margin:5 }}
                            direction={hot_markets_direction=='vertical' ? 'horizontal' : 'vertical'}
                            show_grades={show_grades}
                            market={market}
                            hide_liquidity={hide_liquidity}
                            event_order_stats={[data.item]}
                            latest_trades={athlete_trades}
                            onTradeLongPress={onTradeLongPress}
                            default_price_view={default_price_view}
                            best_available_orders={athlete_orders}
                            onOrder={onOrder}
                            onView={onView}
                            onSetMarket={onSetMarket}
                        />
                )
            case 'event':
                const event_trades = latest_trades.filter(t => t.market_type == 'FOR_MONEY' && t.market_id == data.item.market_id);
                const event_orders = best_available_orders.filter(t => t.market_id == data.item.market_id);
                return (
                    <View transparent>
                        <EventMarket
                            event={event}
                            market={market}
                            float
                            highlight_router_orders={highlight_router_orders}
                            disabled_markets={disabled_markets}
                            style={{ flexGrow:1, margin:5 }}
                            direction={hot_markets_direction=='vertical' ? 'horizontal' : 'vertical'}
                            hide_liquidity={hide_liquidity}
                            onTradeLongPress={onTradeLongPress}
                            event_order_stats={[data.item]}
                            show_grades={show_grades}
                            latest_trades={event_trades}
                            best_available_orders={event_orders}
                            default_price_view={default_price_view}
                            onOrder={onOrder}
                            onView={onView}
                        />
                    </View>
                )
            default: return<></>
        }
    },[athletes.length, JSON.stringify(best_available_orders), JSON.stringify(latest_trades), JSON.stringify(disabled_markets), default_price_view])

    const renderFlashHeaders = (data:{ item:PollProps, index:number }) => {
        const active = upcoming_flash_market?.poll_id == data.item.poll_id ? true : false
        return (
            <Button
                float={active ? true : false}
                style={{ padding:0, opacity:active?1:0.5, justifyContent:'center', alignItems:'center', width:40, height:40, borderRadius:0, borderTopRightRadius:8, borderTopLeftRadius:8, borderWidth:1, borderColor: C.borders.light }}
                onPress={() => setExpandedData({ ...expanded_data, selected_flash: data.item.poll_id })}
            >
                {data.item.poll_image ?
                <Image
                    source={{ uri: data.item.poll_image.url }}
                    style={{ height:40, width:40 }}
                    resizeMode='contain'
                />
                :
                <Icons.FlameIcon size={24} color={C.text.error} />
                }
            </Button>
        )
    }

    const renderPrimaryMarkets = useCallback((data:{ item:MarketProps, index:number }) => {
        const is_locked = MarketComponentHelpers.isEventMarketLocked(event, data.item.market_id);
        return (
            <View transparent>
                <FlatList
                    key={`${event.event_id}_side_options`}
                    scrollEnabled={false}
                    data={data.item.side_options.sort((a,b) => {
                        let a_t = a.side == data.item.trade_side ? 1 : 0
                        let b_t = b.side == data.item.trade_side ? 1 : 0
                        return a_t - b_t
                    })}
                    keyExtractor={(item) => `${event.event_id}:${data.item.market_id}:${item.side}`}
                    renderItem={({ item, index }) => renderSides({ item, index, market:data.item, is_locked })}
                />
            </View>
        )
    },[JSON.stringify(best_available_orders), JSON.stringify(latest_trades), primary_width, JSON.stringify(disabled_markets), default_price_view])

    if(!event.away || !event.home){ return <></> }

    const { liquidity, open_order_count } = useMemo(() => MarketComponentHelpers.getLiqudity(event_order_stats),[JSON.stringify(event_order_stats)])
    const { non_primary_markets, available } = useMemo(() => TeamEventMarketHelpers.sortNonPrimaryMarkets(TeamEventMarketHelpers.getNonPrimaryMarkets(event, markets), event_order_stats, latest_trades),[JSON.stringify(event_order_stats), JSON.stringify(latest_trades), markets.length, event.last_update_datetime])
    const tv_link = event.info?.broadcast?.link && onTVSelect ? event.info?.broadcast?.link : undefined
    return (
        <GuideView prompt_location={help_location} guide={guide} active={guide_active} onComplete={() => {
            setGuide({ guide_active: false })
            if(onHelpToggle){ onHelpToggle(false) }
        }}>

        <View nativeID='team_event_market' ref={ref} float={float} style={[style]}>
            {sponsor ?
            <LinearGradient style={{ marginTop:-1, marginLeft:-1, marginRight:-1, padding:10, flexDirection:'row', alignItems:'center', borderTopRightRadius:8, borderTopLeftRadius:8 }} start={{x: 0, y: 0}} end={{x: 1, y: 0}} colors={[sponsor.brand_primary ?? C.text.h1, sponsor.brand_secondary ?? C.text.h1]}>
                {onActivate && event_status != 'closed' ?
                    <Button
                        float
                        style={{ opacity: activate_loading ? 0.5 : 1, backgroundColor:C.text.success, padding:0, height:34, width:34, justifyContent:'center', alignItems:'center', borderRadius:100 }}
                        disabled={activate_loading}
                        loading={activate_loading} 
                        
                        onPress={() => onActivate(event.event_id, 'team')}
                    >
                        <Icons.RefreshIcon size={20} color={C.text.white} />
                        </Button>
                    :
                    <View transparent style={{ height:30, width:30, borderRadius:4, backgroundColor:Colors.shades.white }}>

                    <Image
                    source={{ uri: sponsor.company_image?.url }}
                    style={{ height:30, width:30, borderRadius:4 }}
                    resizeMode='cover'
                    />
                    </View>
                    }
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text size={12} color={Colors.shades.white} weight='bold'>Brought to you by {sponsor.company_name}</Text>
                    <View transparent style={{ marginTop:3, flexDirection:'row', alignItems:'center' }}> 
                        <View style={{ marginLeft:5 }} transparent>
                            {event.event_sub_title ?
                            <Text size={12} style={{ maxWidth:150 }} theme='description'>{event.event_sub_title}</Text>
                            :<></>}
                            {show_id ?
                            <Text theme='light' selectable>{event.event_id} </Text>
                            :<></>}
                        </View>
                    </View>
                </View>
                <Button transparent disabled={!tv_link} style={{ flexDirection:'row', alignItems:'center', padding:0 }}
                onPress={() => tv_link && onTVSelect ? onTVSelect(tv_link) : console.log('')}>
                    <Icons.TVIcon size={10} color={Colors.shades.white} />
                    <Text style={{ marginLeft:8 }} size={12} color={Colors.shades.white} weight='semibold'>{event.info?.broadcast?.network}</Text>
                </Button>
                
                {onShare ?
                <Button type='text' style={{ marginLeft:10 }} onPress={() => onShare(event)}>
                    <Icons.ShareIcon size={14} color={C.text.action} />
                </Button>
                :<></>}
                {onViewMarketStats ?
                <Button
                    float
                    style={{ backgroundColor:C.text.gold, padding:0, height:34, width:34, justifyContent:'center', alignItems:'center', borderRadius:100 }}
                    disabled={activate_loading}
                    loading={activate_loading} 
                    
                    onPress={() => onViewMarketStats()}
                >
                    <Icons.PieIcon size={16} color={C.text.white} />
                </Button>
                :<></>}
            </LinearGradient>
            :
            <View type='header' nativeID='event_header_data' style={{ flexDirection:'row', alignItems:'center', flexWrap:'wrap', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8, borderBottomWidth:1, borderBottomColor:C.borders.light }}>
                <View transparent style={{ flex:1, flexDirection:'row', alignItems:'center' }}>
                    {onActivate && event_status != 'closed' ?
                    <Button
                        float
                        style={{ opacity: activate_loading ? 0.5 : 1, backgroundColor:C.text.success, padding:0, height:34, width:34, justifyContent:'center', alignItems:'center', borderRadius:100 }}
                        disabled={activate_loading}
                        loading={activate_loading} 
                        
                        onPress={() => onActivate(event.event_id, 'team')}
                    >
                        <Icons.RefreshIcon size={20} color={C.text.white} />
                    </Button>
                    : league ?
                    <Image
                        source={{ uri: league.league_image }}
                        style={{ height:30, width:30, borderRadius:2, marginRight:4 }}
                        resizeMode='cover'
                    />
                    :<></>}
                    
                    <View style={{ marginLeft:5 }} transparent>
                        {event.event_sub_title ?
                        <Text size={12} style={{ maxWidth:150 }} theme='description'>{event.event_sub_title}</Text>
                        :<></>}
                        {show_id ?
                        <Text theme='light' selectable>{event.event_id}</Text>
                        :<></>}
                    </View>
                    {show_help ?
                    <GuideButton
                        onError={() => alert('Unable to get help')}
                        onHelp={(guide) => {
                            setGuide({ guide_active:true, guide });
                            if(onHelpToggle){ onHelpToggle(true) }
                        }}
                        show_label
                        client_native_id='team_event_market'
                    />
                    :<></>}
                </View>
                <Button transparent disabled={!tv_link} style={{ flexDirection:'row', alignItems:'center', padding:0 }}
                onPress={() => tv_link && onTVSelect ? onTVSelect(tv_link) : console.log('')}>
                    <Icons.TVIcon size={10} color={tv_link?C.text.action:C.text.h1} />
                    <Text style={{ marginLeft:8 }} size={12} color={tv_link?C.text.action:C.text.h1} weight='semibold'>{event.info?.broadcast?.network}</Text>
                </Button>
                
                
                {onShare ?
                <Button type='text' style={{ marginLeft:10 }} onPress={() => onShare(event)}>
                    <Icons.ShareIcon size={14} color={C.text.action} />
                </Button>
                :<></>}
                {onViewMarketStats ?
                <Button
                    float
                    style={{ backgroundColor:C.text.gold, padding:0, height:34, width:34, justifyContent:'center', alignItems:'center', borderRadius:100 }}
                    disabled={activate_loading}
                    loading={activate_loading} 
                    
                    onPress={() => onViewMarketStats()}
                >
                    <Icons.PieIcon size={16} color={C.text.white} />
                </Button>
                :<></>}
            </View>
            }
            <View type='body' nativeID='primary_markets' style={{ flexDirection:'row' }}>
                <View transparent nativeID='team_details' style={{ flex:1, padding:5, paddingRight:0 }}>
                    <View transparent style={{ flexDirection:'row', marginTop:5, marginBottom:5, marginLeft:10 }}>
                        {show_stats ?
                        <Button
                            title='X'
                            style={{ marginRight:5, padding:10 }}
                            type='error'
                            title_color={Colors.utility.error}
                            onPress={() => setShowStats(!show_stats)}
                        />
                        :<></>}
                        {!show_stats ?
                            <View style={{ marginRight:5, height:10, width:10, borderRadius:100, backgroundColor:event_status=='active'?C.text.success:event_status=='inactive'?C.text.action:C.text.error }} />
                        :<></>}
                        
                        <Text size={12} color={show_stats ? C.text.error : C.text.action}>{show_stats ? 'Hide Boxscore': `${TeamEventMarketHelpers.getTimeDetail(event)}`}</Text>
                    </View>
                    <Button 
                        disabled={!onTeamSelect} 
                        style={{ ...view_styles.body_row, minHeight:48, flexGrow:1 }} 
                        type='text'
                        onPress={() => onTeamSelect ? onTeamSelect(event.away_team_id) : console.log('')}
                        >
                        <Image
                            source={{ uri: event.away.image?.url }}
                            style={{ height:30, width:30 }}
                            resizeMode='cover'
                        />
                        <View transparent style={{ flex:1, flexDirection:'row', alignItems:'center', marginLeft:5 }}>
                            {event.away.rank ?
                            <Text style={{ marginRight:3 }} size={12} color={C.text.highlight} weight='semibold'>{event.away.rank}</Text>
                            :<></>}
                            <View transparent style={{ flex:1, marginRight:5 }}>
                                <Text theme='h2'>{event.away.market_name} {event.away.name}</Text>
                                {event.away_sub_title ?
                                <Text style={{ marginTop:3 }} theme='description'>{event.away_sub_title}</Text>
                                :<></>}
                            </View>
                            <Text theme='description'>{event.away_team_score}</Text>
                        </View>
                    </Button>
                    <Button 
                        disabled={!onTeamSelect} 
                        style={{ ...view_styles.body_row, minHeight:48, flexGrow:1 }} 
                        type='text'
                        onPress={() => onTeamSelect ? onTeamSelect(event.home_team_id) : console.log('')}
                        >
                        <Image
                            source={{ uri: event.home.image?.url }}
                            style={{ height:30, width:30 }}
                            resizeMode='cover'
                        />
                        <View transparent style={{ flex:1, flexDirection:'row', alignItems:'center', marginLeft:5 }}>
                            {event.home.rank ?
                            <Text style={{ marginRight:3 }} size={12} color={C.text.highlight} weight='semibold'>{event.home.rank}</Text>
                            :<></>}
                            <View transparent style={{ flex:1, marginRight:5 }}>
                                <Text theme='h2'>{event.home.market_name} {event.home.name}</Text>
                                {event.home_sub_title ?
                                <Text style={{ marginTop:3 }} theme='description'>{event.home_sub_title}</Text>
                                :<></>}
                            </View>
                            <Text theme='description'>{event.home_team_score}</Text>
                        </View>
                    </Button>
                </View>
                {event_status == 'active' ?
                <View transparent nativeID='primary_market_details' style={{ padding:5, flex:1 }} onLayout={(ev) => {
                    const { width } = ev.nativeEvent.layout
                    setMarketWidth(width);
                }}>
                    <View nativeID='primary_market_headers' transparent style={{ marginTop:5, marginBottom:5 }}>
                        <FlatList
                            key={`${event.event_id}_market_headers`}
                            data={primary_markets}
                            keyExtractor={(item) => item.market_id.toString()}
                            horizontal
                            scrollEnabled={false}
                            renderItem={renderMarketHeaders}
                        />
                    </View>
                    <View transparent nativeID='event_primary_markets'>
                    <FlatList
                        key={`${event.event_id}_primary_markets`}
                        data={primary_markets.sort((a,b) => parseInt(a.market_id) - parseInt(b.market_id))}
                        horizontal
                        scrollEnabled={false}
                        renderItem={renderPrimaryMarkets}
                        keyExtractor={(item) => item.market_id.toString()}
                    />
                    </View>
                </View>
                : 
                <Button 
                    disabled={!onActivate ? true: false} 
                    style={{ flex:0.5, justifyContent:'center', alignItems:'center' }} 
                    type='text'
                    transparent
                    onPress={() => onActivate ? onActivate(event.event_id, 'team') : console.log('')}
                    >
                    <Icons.MarketClosedIcon size={60} color={C.text.h1} />
                </Button>
                }
            </View>
            {flash_markets && onFlashSelect && flash_markets.length > 1 ?
            <View  style={{ borderTopWidth:1, borderColor:C.borders.light, paddingTop:5 }}>
                <FlatList
                    data={flash_markets.sort((a,b) => (a.total_responses??0) - (b.total_responses??0) || moment(a.end_datetime).unix() - moment(b.end_datetime).unix())}
                    key='flash_markets_event'
                    horizontal
                    keyExtractor={(item) => item.poll_id.toString()}
                    renderItem={renderFlashHeaders}
                />
            </View>
            :<></>}
            {onFlashSelect && upcoming_flash_market ?
            <View nativeID='team_event_flash_markets' type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomWidth:1, borderColor:C.borders.light }}>
                {upcoming_flash_market.poll_image && flash_markets && flash_markets.length == 1 ?
                <Image
                    source={{ uri: upcoming_flash_market.poll_image.url }}
                    style={{ height:30, width:30, borderRadius:4 }}
                    resizeMode='cover'
                />
                : flash_markets && flash_markets.length == 1 ?
                <Icons.FlameIcon size={24} color={C.text.error} />
                :<></>}
                <View transparent style={{ flex:1, marginLeft:flash_markets && flash_markets.length == 1 ? 10: 0 , marginRight:10 }}>
                    <Text theme='h2'>{upcoming_flash_market.poll_question}</Text>
                </View>
                <Button
                    title='BUY NOW'
                    type='success'
                    style={{ padding:10 }}
                    onPress={() => onFlashSelect(upcoming_flash_market.poll_id)}
                />
            </View>
            :<></>}
            {event_status == 'active' ?
            <View type='footer' nativeID='team_event_activities' style={{ padding:10, flexDirection:'row', alignItems:'center', flexWrap:'wrap', borderBottomLeftRadius:8, borderBottomRightRadius:8 }}>
                {!hide_liquidity ?
                <Button 
                nativeID='team_event_liquidity'
                style={{ flex:1, flexDirection:'row', minWidth:115, padding:0 }} 
                type='text'
                
                onPress={() => {
                    if(!primary_markets[0]){ return }
                    onView({ event_id: event.event_id, event_type:'team', market_id: primary_markets[0].market_id });
                }}>
                    <View transparent float style={{ height:30, width:30, borderRadius:100, justifyContent:'center', alignItems:'center' }}>
                        <Icons.LiquidityIcon color={C.text.h1} size={30}/>
                    </View>
                    <View transparent style={{ marginLeft:5 }}>
                        <Text theme='description'>Liquidity</Text>
                        <Text size={12} style={{ marginTop:3 }} color={C.text.highlight} weight='semibold'>${liquidity.toFixed()} {open_order_count} Orders</Text>
                    </View>
                </Button>
                :<></>}
                {squares_competitions && squares_competitions.length > 0 ?
                <Button 
                    float
                    type='squares'
                    nativeID='team_event_squares'
                    style={{ marginRight:3, flexDirection:'row', alignItems:'center', padding:10, borderRadius:22 }}
                    onPress={() => handleSelectSquares()}>
                    <Icons.SquaresIcon size={18} color={C.text.white} />
                    <Text style={{ marginLeft:5 }} size={14} color={C.text.white} weight='semibold'>Squares</Text>
                </Button>
                :<></>}
                {onParlaySelect && parlays && parlays.length > 0 ?
                <Button 
                    float
                    style={{ marginRight:3, flexDirection:'row', alignItems:'center', padding:10, borderRadius:22, backgroundColor:C.text.gold }}
                    onPress={() => handleSelectParlay()}>
                    <Icons.ParlayIcon size={14} color={C.text.white} />
                    {!squares_competitions || squares_competitions.length == 0 ?
                    <Text theme='h2' style={{ marginLeft:5 }} color={C.text.white}>Parlays</Text>
                    :<></>}
                </Button>
                :<></>}
                {competitions && competitions.length > 0 ?
                <Button 
                    float
                    type='competition'
                    nativeID='team_event_competitions'
                    style={{ marginRight:3, flexDirection:'row', alignItems:'center', padding:10, borderRadius:22 }}
                    onPress={() => handleSelectCompetition()}>
                    <Icons.PickEmIcon size={14} color={C.text.white} />
                    {!parlays || parlays.length == 0 ?
                    <Text style={{ marginLeft:5 }} size={14} color={C.text.white} weight='semibold'>MultiPick</Text>
                    :<></>}
                </Button>
                :<></>}
                {non_primary_markets.length > 0 && !hide_non_primary ?
                <Button 
                    float
                    nativeID='team_event_non_primary'
                    style={{ height:35, width:35, justifyContent:'center', borderRadius:100, alignItems:'center' }} 
                    onPress={() => expandedEvent()}
                >
                   <Icons.ChevronIcon direction={expanded ? 'up' : 'down'} size={8} color={C.text.h1} />
                </Button>
                :available && !expanded && !hide_non_primary ?
                <Button
                    nativeID='team_event_non_primary'
                    title='MORE'
                    type='info'
                    style={{ padding:10 }}
                    onPress={() => onViewAdditionalMarkets(event.event_id, 'team')}
                />
                :<></>}
                {competitions && competitions_expanded ?
                <View float style={{ position:'absolute', bottom:0, right:0, justifyContent:'flex-end', maxHeight:230, minWidth:300 }}>
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:5 }}>
                        <Icons.PickEmIcon size={16} color={C.text.h1} />
                        <Text style={{ flex:1, marginLeft:10 }} theme='h2'>Select Competition</Text>
                        <Button
                            title='X'
                            style={{ padding:10, borderRadius:100 }}
                            type='error'
                            onPress={() => setCompetitionsExpanded(false)}
                        />
                    </View>
                    <ScrollView style={{flex:1}}>
                        <FlatList
                            data={competitions}
                            renderItem={renderCompetitions}
                            keyExtractor={(item) => item.competition_id.toString()}
                        />
                    </ScrollView>
                </View>
                :<></>}
                {parlays && parlays_expanded ?
                <View float style={{ position:'absolute', bottom:0, right:0, justifyContent:'flex-end', maxHeight:230, minWidth:300, maxWidth:350 }}>
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:5 }}>
                        <Icons.ParlayIcon size={18} color={C.text.h1} />
                        <Text style={{ flex:1, marginLeft:10 }} theme='h2'>Available Parlays</Text>
                        <Button
                            title='X'
                            style={{ padding:10, borderRadius:100 }}
                            type='error'
                            onPress={() => setParlaysExpanded(false)}
                        />
                    </View>
                        <FlatList
                            data={parlays}
                            renderItem={renderParlays}
                            keyExtractor={(item) => item.parlay_id.toString()}
                        />
                </View>
                :<></>}
                {squares_competitions && squares_expanded ?
                <View float style={{ position:'absolute', bottom:0, right:0, justifyContent:'flex-end', maxHeight:230, minWidth:300 }}>
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:5 }}>
                        <Icons.PickEmIcon size={16} color={C.text.h1} />
                        <Text style={{ flex:1, marginLeft:10 }} theme='h2'>Select Squares</Text>
                        <Button
                            title='X'
                            style={{ padding:10, borderRadius:100 }}
                            type='error'
                            onPress={() => setSquaresExpanded(false)}
                        />
                    </View>
                    <ScrollView style={{flex:1}}>
                        <FlatList
                            data={squares_competitions}
                            renderItem={renderSquaresCompetitions}
                            keyExtractor={(item) => item.sq_comp_id.toString()}
                        />
                    </ScrollView>
                </View>
                :<></>}
            </View>
            :<></>}
            {expanded && !hide_non_primary ?
            <View transparent>
                <View transparent style={{ flexDirection:'row', alignItems:'center' }}>
                    <View transparent style={{ flexGrow:1 }}>
                        <LinearDiagnal label_size={12} label={'Hot Markets'} left_color={Colors.utility.warning} right_color={Colors.utility.error}/>
                    </View>
                    {loading ?
                        <ActivityIndicator style={{ paddingRight:20 }} size='small' color={Colors.brand.midnight} /> 
                    :
                    <Button
                        title='SEE ALL'
                        type='text'
                        onPress={() => onViewAdditionalMarkets(event.event_id, 'team')}
                    />
                    }
                </View>
                <View transparent>
                    <FlatList
                        data={non_primary_markets}
                        renderItem={renderNonPrimaryMarkets}
                        showsHorizontalScrollIndicator={false}
                        keyExtractor={(item) => `${item.market_id}:${item.side_type}:${item.side_id}`}
                        horizontal={hot_markets_direction && hot_markets_direction == 'vertical' ? false : true}
                    />
                </View>
                {podcasts.length > 0 ?
                <View type='footer' style={{ flexDirection:'row', marginTop:10, borderBottomRightRadius:8, borderBottomLeftRadius:8 }}>
                    <View transparent style={{ padding:5, paddingRight:20, paddingLeft:20, justifyContent:'center', borderBottomLeftRadius:8 }}>
                        <Icons.HeadphoneIcon size={36} color={C.text.h1} />
                    </View>
                    <View transparent style={{ flex:1, padding:5 }}>
                        <Text style={{ marginBottom:3 }} theme='h2'>Need Help? Checkout these podcasts!</Text>
                        <FlatList
                            key={`${event.event_id}:pods`}
                            data={podcasts}
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            renderItem={renderPodcasts}
                            keyExtractor={(item) => item.podcast_id.toString()}
                        />
                    </View>
                </View>  
                :<></>}
            </View>
            :<></>}
            {show_stats ?
            <View style={{ borderTopWidth:1, borderColor:Colors.shades.shade600 }}>
                <ContestStatsCard
                    event={event}
                />
            </View>
            :<></>}
        </View>
        </GuideView>
    )
}



export default React.memo(TeamEventMarket, (left, right) => {
    if(JSON.stringify(left) != JSON.stringify(right)){ return false }
    return true
})